import isEmail from 'validator/lib/isEmail';
import equals from 'validator/lib/equals';
import messages from './messages';
import type { ValidatorOptions } from './types';
import { clearMessages, getLang } from './utils';

const ERROR_CLASS = 'is-error';

export { isEmail, equals };

const phoneRegex = /^\+?\d{7,14}$/;

export function isPhone(string: string): boolean {
    // return /^\d{3}-\d{3}-\d{4}$/i.test(string);
    return phoneRegex.test(string);
}

function isPhoneInput(input: HTMLInputElement) {
    return input.type === 'tel' || input.classList.contains('js-validate--phone');
}

function isEmailInput(input: HTMLInputElement) {
    return input.type === 'email' || input.classList.contains('js-validate--email');
}

function isCheckboxInput(input: HTMLInputElement) {
    return input.type === 'checkbox';
}

function isSelectInput(input: HTMLInputElement) {
    return input.tagName === 'select' || input.classList.contains('js-validate--select');
}

function isEmptyInput(input: HTMLInputElement) {
    return input.value.trim().length === 0;
}

export function validateInput(input: HTMLInputElement, container: HTMLElement | Document = document): boolean {
    // Кастомная валидация через регулярное выражение
    if (input.classList.contains('js-validate--custom')) {
        const regExp = input.getAttribute('data-custom-validation') || '.*';
        return new RegExp(regExp, 'i').test(input.value);
    }

    // Валидация электронной почты
    if (isEmailInput(input)) {
        return input.hasAttribute('required') || input.value.trim().length > 0 ? isEmail(input.value) : true;
    }

    // Валидация телефона
    if (isPhoneInput(input)) {
        return input.hasAttribute('required') || input.value.trim().length > 0 ? isPhone(input.value) : true;
    }

    // Валидация чекбокса
    if (isCheckboxInput(input)) {
        return input.hasAttribute('required') ? input.checked : true;
    }

    // Валидация селекта
    if (isSelectInput(input)) {
        const options = Array.from(input.querySelectorAll('option'));
        const selectedOptions = options.filter(
            (option) =>
                option.selected &&
                !option.hasAttribute('placeholder') &&
                option.innerText !== input.getAttribute('placeholder'),
        );
        return input.hasAttribute('required') ? !!selectedOptions.length : true;
    }

    // Валидация равенства двух строк
    if (input.classList.contains('js-validate--equivalent')) {
        if (!input.hasAttribute('data-equivalent-name')) {
            return false;
        }
        const fieldName = input.getAttribute('data-equivalent-name');
        const field = container.querySelector<HTMLInputElement>(`[name="${fieldName}"]`);
        return field ? equals(input.value, field.value) : false;
    }

    // Валидация заполненности поля
    if (input.hasAttribute('required') && isEmptyInput(input)) {
        return false;
    }

    // Default
    return true;
}

const DEFAULT_OPTIONS: ValidatorOptions = {
    inputSelector:
        'input[name]:not([type="submit"]):not([type="reset"]):not([type="hidden"]), select[name]:not([type="submit"]):not([type="reset"]):not([type="hidden"]), textarea[name]:not([type="submit"]):not([type="reset"]):not([type="hidden"])',
    messages,
    scrollToInvalidInput: true,
};

export default (form: HTMLFormElement, validatorOptions: Partial<ValidatorOptions> = DEFAULT_OPTIONS) => {
    const options: ValidatorOptions = { ...DEFAULT_OPTIONS, ...validatorOptions };
    form.setAttribute('novalidate', 'novalidate');
    const inputs = Array.from(form.querySelectorAll<HTMLInputElement>(options.inputSelector));

    function clearInput(input: HTMLInputElement) {
        const messageElement = input.parentElement?.querySelector('.app-message');

        if (messageElement) {
            messageElement.textContent = '';
        }

        input.classList.remove(ERROR_CLASS);

        if (input.classList.contains('textarea')) {
            const wrapper = input.closest('.textarea-wrapper');
            wrapper?.classList.remove(ERROR_CLASS);
        }
    }

    function validate() {
        let isFormValid = true;
        const lang = getLang();
        const erroredInputs: HTMLInputElement[] = [];

        clearMessages(form);

        inputs.forEach((input) => {
            const isValid = validateInput(input, form);
            // const choicesDiv = input.closest('div.choices');
            const messageElement = /* choicesDiv ||  */ input.parentElement?.querySelector('.app-message');

            if (isValid) {
                input.classList.remove(ERROR_CLASS);
                input.closest('.js-input-wrapper')?.classList.remove(ERROR_CLASS);

                // if (choicesDiv) {
                //     choicesDiv.classList.remove(ERROR_CLASS);
                // }
            } else {
                isFormValid = false;
                erroredInputs.push(input);
                input.classList.add(ERROR_CLASS);
                input.closest('.js-input-wrapper')?.classList.add(ERROR_CLASS);

                // if (choicesDiv) {
                //     choicesDiv.classList.add(ERROR_CLASS);
                // }

                if (messageElement) {
                    messageElement.textContent = '';

                    switch (true) {
                        case isEmptyInput(input) && input.hasAttribute('required'):
                            messageElement.textContent = options.messages[lang].EMPTY_FIELD;
                            break;
                        case isEmailInput(input):
                            messageElement.textContent = options.messages[lang].INVALIDATED_EMAIL;
                            break;
                        case isPhoneInput(input):
                            messageElement.textContent = options.messages[lang].INVALIDATED_PHONE;
                            break;
                        case isCheckboxInput(input):
                            messageElement.textContent = options.messages[lang].INVALIDATED_CHECKBOX;
                            break;
                        case isSelectInput(input):
                            messageElement.textContent = options.messages[lang].INVALIDATED_SELECT;
                            break;
                        case input.classList.contains('js-validate--equivalent'):
                            messageElement.textContent = options.messages[lang].INVALIDATED_EQUALS;
                            break;
                        case input.classList.contains('js-validate--custom') &&
                            input.hasAttribute('data-custom-validation-error-message'):
                            messageElement.textContent = input.getAttribute('data-custom-validation-error-message');
                            break;
                        default:
                            break;
                    }
                }
            }
        });

        if (options.scrollToInvalidInput && erroredInputs[0]) {
            erroredInputs[0].scrollIntoView(options.scrollToInvalidInputOptions);
        }

        if (isFormValid) {
            options.onValidationSuccess?.();
        } else {
            options.onValidationError?.();
        }

        options.onValidationComplete?.();

        return isFormValid;
    }

    return { validate, form, inputs, clearInput };
};
